import { DateTime } from 'luxon'

import { Box, Paper, Theme, Tooltip, Typography, useTheme } from '@mui/material'

import {
  ProfileSize,
  getNameStyles,
  getProfileContainerStyles,
  getProfileImageContainerStyle,
  getProfileIntervalStyles,
  getProfileNameStyle,
  getProfileStatusStyle,
} from 'styles/profile.styles'

import { ClassLevelDisplay } from 'components/ClassLevelDisplay'
import { ProfileIntervalDelimiter } from 'components/ProfileIntervalDelimiter'
import { TodayOnlyHours } from 'components/TodayOnlyHours'

import { ON_DUTY_COLOR } from 'constants/colors'
import { DISPLAY_SMALL, LUXON_HUMAN_TIME_FORMAT } from 'constants/formatting'

import { getShiftStatusColor, getTextShadowStringByStatus } from 'styles/shift.styles'

import { Event } from '@masaok/scanner-shared-next-public/types/event.types'
import { ShiftStatus } from 'types/supabase.database.custom.types'

import { extractCourseFromEventSummary } from 'utils/events'
import { getShiftStatus } from 'utils/shifts'

import { SessionProfile, Student } from 'types/student.types'
import { getVersionTooltip } from 'config/version'

const useStyles = (theme: Theme) => ({
  root: {
    display: 'flex',
    flex: 1,
    justifyContent: 'space-evenly',
    padding: theme.spacing(1),
    margin: theme.spacing(0.5),
  },

  paper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: theme.spacing(0.5),
    padding: theme.spacing(1.5),
    ...getProfileContainerStyles(theme),
    minHeight: 'auto',
  },

  // Logo (required for fixed-ratio responsive images)
  imageContainer: {
    height: '13.5vh', // reduced by 25% from 18vh for more compact layout
    overflow: 'hidden', // ensure images are cropped by container
  },

  placeholder: {
    ...getProfileContainerStyles(theme),
    height: '100%', // make image 33% larger so bottom gets cropped by container
    width: '100%',
    objectFit: 'cover' as const,
    objectPosition: 'center top' as const,
  },

  // Text stuff - all reduced font sizes and spacing
  profileName: {
    ...getNameStyles(theme),
    fontSize: '2.2vw', // reduced from 3.0vw
    lineHeight: '2.8vw', // reduced from 4.5vw
    marginBottom: theme.spacing(0.5),
  },

  classOf: {
    ...getNameStyles(theme),
    fontSize: '1.3vw', // reduced from 1.75vw
    marginBottom: theme.spacing(0.25), // reduced spacing
    color: theme.palette.grey[700],
  },

  course: {
    ...getNameStyles(theme),
    fontSize: '1.3vw', // reduced from 1.75vw
    marginBottom: theme.spacing(0.25), // reduced spacing
    color: theme.palette.grey[700],
  },

  timeframe: {
    ...getProfileIntervalStyles(theme),
    fontSize: '1.5vw', // reduced from 2.0vw
    marginBottom: theme.spacing(0.5),
  },

  description: {
    fontWeight: 'bold',
    fontSize: '0.8rem', // reduced from 1.0rem
    marginTop: theme.spacing(1), // reduced from 2
    color: theme.palette.grey[700],
  },

  status: {
    fontWeight: 'bold',
    fontSize: '2.2vw', // reduced from 3vw
    textTransform: 'uppercase',
    color: ON_DUTY_COLOR,
  },
})

type ProfileCardCompactProps = {
  event?: Event
  image?: string
  tutor?: Student
  profile?: SessionProfile
  size?: ProfileSize
}

export const ProfileCardCompact = (props: ProfileCardCompactProps) => {
  const classes = useStyles(useTheme())
  const { size, profile } = props

  const shiftStatus = getShiftStatus(
    profile?.inputDateTimeIso as string,
    profile?.event as Event,
    profile?.shift?.shift_status as ShiftStatus
  )

  return (
    <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{getVersionTooltip()}</span>} placement="bottom">
    <Paper sx={classes.paper} elevation={10}>
      <Box>
        <Box sx={{ ...classes.imageContainer }} style={{ height: '30vh' }}>
          {profile?.imageUrl && (
            <img style={{ ...classes.placeholder }} src={profile.imageUrl} alt="logo" />
          )}
        </Box>
      </Box>
      <Typography sx={classes.profileName} style={getProfileNameStyle(size)}>
        {profile?.tutor?.nickname || profile?.tutor?.firstName}
      </Typography>

      {/* Display class and course on separate lines */}
      <Typography sx={classes.classOf}>
        <ClassLevelDisplay tutor={profile?.tutor as Student} />
      </Typography>
      <Typography sx={classes.course}>
        {extractCourseFromEventSummary(profile?.event.summary as string)}
      </Typography>

      <Typography sx={classes.timeframe}>
        {DateTime.fromISO(profile?.event.startISO as string).toFormat(LUXON_HUMAN_TIME_FORMAT)}
        <ProfileIntervalDelimiter size={size} />
        {DateTime.fromISO(profile?.event.endISO as string).toFormat(LUXON_HUMAN_TIME_FORMAT)}
      </Typography>
      <TodayOnlyHours event={profile?.event} />
      <Typography
        sx={{
          ...getProfileStatusStyle(size),
          fontWeight: 'bold',
          color: getShiftStatusColor(shiftStatus, { profile: true }),
          textShadow: getTextShadowStringByStatus(shiftStatus, { profile: true }),
          fontSize: '2.2vw', // ensure status text is also compact
        }}
      >
        {shiftStatus}
      </Typography>
    </Paper>
    </Tooltip>
  )
}
